import React, { useEffect, useRef, useState } from 'react';
import './AboutPage.css';
import { 
    FaTruck, FaCalculator, FaTools, FaHandHoldingUsd, FaBullseye, FaRocket, 
    FaMapMarkerAlt, FaCube, FaBuilding, FaHandsHelping, FaCheckCircle 
} from 'react-icons/fa';

// Import gambar secara langsung
import heroImage from './xyz.PNG';
import logoImage from '../../Assest/sumber-lancar.png';

// Fallback image jika gambar utama tidak dimuat
const FALLBACK_HERO_IMAGE = 'https://images.unsplash.com/photo-1503387762353-8c6637f89308?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
const FALLBACK_LOGO = 'https://via.placeholder.com/250x150/006633/FFFFFF?text=Sumber+Lancar';

// --- DATA ---
const WHY_CHOOSE_US_ITEMS = [
    { icon: FaCheckCircle, title: "Kualitas Terjamin", text: "Hanya produk asli & standar SNI." },
    { icon: FaCalculator, title: "Harga Kompetitif", text: "Transparan, sesuai pasar, tanpa biaya tersembunyi." },
    { icon: FaHandHoldingUsd, title: "Layanan Profesional", text: "Tim ramah, solutif, dan siap membantu." },
    { icon: FaTools, title: "Stok Lengkap", text: "Kebutuhan material kecil hingga besar selalu tersedia." },
    { icon: FaTruck, title: "Pengiriman Tepat Waktu", text: "Ke lokasi proyek Anda, tanpa menunda." },
];

const PRODUCT_CATEGORIES = [
    { icon: FaBuilding, title: "Besi & Baja", items: ["Besi beton", "Wiremesh", "Pipa", "Plat, UNP, H-Beam, CNP"] },
    { icon: FaCube, title: "Material Bangunan", items: ["Semen", "Pasir", "Bata ringan, batako", "Keramik"] },
    { icon: FaTools, title: "Perlengkapan & Finishing", items: ["Cat", "Kawat, paku", "Peralatan tukang"] },
    { icon: FaHandsHelping, title: "Layanan Tambahan", items: ["Jasa pemotongan besi", "Pengiriman cepat & tepat waktu"] },
];

const TRUST_RECORDS = [
    { title: "Pembangunan Perumahan & Hunian Pribadi", text: "Mitra terpercaya dalam mewujudkan rumah impian Anda." },
    { title: "Proyek Gedung Perkantoran & Pertokoan", text: "Mendukung konstruksi bangunan komersial dengan material terbaik." },
    { title: "Renovasi Rumah & Bangunan Skala Menengah", text: "Menyediakan solusi material untuk perubahan yang signifikan." },
    { title: "Penyediaan Material untuk Kontraktor & Developer", text: "Partner strategis dalam pasokan material proyek besar." },
];

// --- CUSTOM HOOK ---
const useScrollAnimation = () => {
    const observer = useRef(null);

    useEffect(() => {
        observer.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('about-visible');
                }
            });
        }, { threshold: 0.1 });

        return () => observer.current?.disconnect();
    }, []);

    return (el) => {
        if (el && observer.current) {
            observer.current.observe(el);
        }
    };
};

// --- CHILD COMPONENTS ---
const AnimatedSection = ({ children, observe, className = '', id = '' }) => (
    <section id={id} className={`about-section ${className}`} ref={observe}>
        {children}
    </section>
);

const HeroSection = ({ observe }) => {
    const [currentHeroImage, setCurrentHeroImage] = useState(heroImage);
    
    // Fallback jika gambar tidak dimuat
    useEffect(() => {
        const img = new Image();
        img.src = heroImage;
        img.onload = () => setCurrentHeroImage(heroImage);
        img.onerror = () => setCurrentHeroImage(FALLBACK_HERO_IMAGE);
    }, []);

    return (
        <header 
            className="about-hero" 
            ref={observe}
            style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7)), url(${currentHeroImage})` }}
        >
            <div className="about-hero-overlay">
                <div className="about-hero-text">
                    <h1>Toko Besi & Bahan Bangunan Sumber Lancar</h1>
                    <p>Membangun kepercayaan, bukan sekadar bangunan. Mitra setia Anda sejak 2018.</p>
                </div>
            </div>
        </header>
    );
};

const AboutUsSection = () => {
    const [currentLogoImage, setCurrentLogoImage] = useState(logoImage);
    
    // Fallback jika gambar tidak dimuat
    useEffect(() => {
        const img = new Image();
        img.src = logoImage;
        img.onload = () => setCurrentLogoImage(logoImage);
        img.onerror = () => setCurrentLogoImage(FALLBACK_LOGO);
    }, []);

    return (
        <div className="about-info-grid">
            <div className="about-info-text">
                <p>Membangun bukan sekadar menyusun bata dan besi. Membangun adalah tentang mewujudkan mimpi, menciptakan ruang, dan menanamkan kepercayaan.</p>
                <p>Sejak berdiri pada tahun 2018, Toko Besi & Bahan Bangunan Sumber Lancar hadir sebagai mitra setia dalam setiap pembangunan. Kami percaya, setiap proyek besar berawal dari material yang berkualitas. Karena itu, kami berkomitmen untuk menyediakan produk terbaik, layanan terpercaya, dan solusi yang memudahkan pelanggan dalam mewujudkan setiap rencana menjadi kenyataan.</p>
            </div>
            <div className="about-info-logo">
                <img 
                    src={currentLogoImage} 
                    alt="Logo Toko Sumber Lancar" 
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_LOGO;
                    }} 
                />
            </div>
        </div>
    );
};

const VisionMissionSection = () => (
    <div className="about-vision-mission">
        <div className="about-card">
            <h3><FaBullseye /> Visi</h3>
            <p>Menjadi toko besi dan bahan bangunan yang terdepan, terpercaya, dan berkontribusi nyata bagi perkembangan pembangunan di Indonesia.</p>
        </div>
        <div className="about-card">
            <h3><FaRocket /> Misi</h3>
            <ul>
                <li>Menyediakan material yang berstandar tinggi dan terjamin kualitasnya.</li>
                <li>Memberikan pelayanan profesional, ramah, dan solutif.</li>
                <li>Menjadi mitra yang mendukung keberhasilan setiap proyek, besar maupun kecil.</li>
                <li>Menjaga kepercayaan pelanggan dengan integritas dan konsistensi.</li>
            </ul>
        </div>
    </div>
);

const ServiceCard = ({ icon: Icon, title, text }) => (
    <div className="about-service-card">
        <Icon className="about-service-icon" />
        <h3>{title}</h3>
        <p>{text}</p>
    </div>
);

const ProductCategoryCard = ({ icon: Icon, title, items }) => (
    <div className="about-product-card">
        <Icon className="about-product-icon" />
        <h3>{title}</h3>
        <ul>
            {items.map(item => <li key={item}>{item}</li>)}
        </ul>
    </div>
);

const TrustRecordCard = ({ title, text }) => (
    <div className="about-trust-card">
        <h4>{title}</h4>
        <p>{text}</p>
    </div>
);

// --- MAIN COMPONENT ---
const AboutPage = () => {
    const observeElement = useScrollAnimation();

    return (
        <div className="about-container">
            <HeroSection observe={observeElement} />

            <main className="about-main">
                <AnimatedSection id="about" observe={observeElement}>
                    <h2 className="about-title">Tentang Kami</h2>
                    <AboutUsSection />
                </AnimatedSection>
                
                <AnimatedSection observe={observeElement} className="about-highlight">
                    <VisionMissionSection />
                </AnimatedSection>

                <AnimatedSection id="products-services" observe={observeElement}>
                    <h2 className="about-title">Produk dan Layanan</h2>
                    <div className="about-products">
                        <p>Kami menyediakan material lengkap untuk semua kebutuhan konstruksi dan renovasi:</p>
                        <div className="about-products-grid">
                            {PRODUCT_CATEGORIES.map(category => <ProductCategoryCard key={category.title} {...category} />)}
                        </div>
                        <p className="about-products-note">Produk terjamin keaslian, kualitas, dan ketahanan.</p>
                    </div>
                </AnimatedSection>

                <AnimatedSection id="why-choose-us" observe={observeElement} className="about-highlight">
                    <h2 className="about-title">Mengapa Memilih Kami?</h2>
                    <p className="about-subtitle">Kami bukan sekadar toko, kami adalah partner pembangunan Anda.</p>
                    <div className="about-services-grid">
                        {WHY_CHOOSE_US_ITEMS.map(item => <ServiceCard key={item.title} {...item} />)}
                    </div>
                </AnimatedSection>

                <AnimatedSection id="trust-record" observe={observeElement}>
                    <h2 className="about-title">Jejak Kepercayaan</h2>
                    <p className="about-subtitle">Kepercayaan pelanggan adalah pondasi terbesar kami.</p>
                    <div className="about-trust-grid">
                        {TRUST_RECORDS.map(record => <TrustRecordCard key={record.title} {...record} />)}
                    </div>
                    <p className="about-trust-note">Setiap transaksi adalah hubungan jangka panjang, bukan sekadar jual-beli.</p>
                </AnimatedSection>

                <AnimatedSection id="contact" observe={observeElement} className="about-map">
                    <h2 className="about-title"><FaMapMarkerAlt /> Lokasi Kami</h2>
                    <div className="about-map-container">
                        <iframe 
                            src="https://www.google.com/maps/embed?pb=!3m2!1sid!2sid!4v1754319994781!5m2!1sid!2sid!6m8!1m7!1skmPp1Qq2ICiTDb6LOF9fEw!2m2!1d-7.303606538540232!2d110.509885622303!3f249.94541417974463!4f10.243810094954682!5f0.7820865974627469"                             
                             width="100%" 
                            height="450" 
                            style={{border:0}} 
                            allowFullScreen 
                            loading="lazy" 
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Lokasi Toko Sumber Lancar"
                        ></iframe>
                    </div>
                </AnimatedSection>
            </main>
        </div>
    );
};

export default AboutPage;